import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { CreditCard, FileText, ExternalLink, Download, Calendar, CheckCircle, AlertCircle, Clock } from 'lucide-react';

export default function BillingSection() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => { loadBilling(); }, []);

  const loadBilling = async () => {
    try {
      const r = await api.get('/api/payments/billing-info');
      setBilling(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const r = await api.post('/api/payments/customer-portal');
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Unable to open billing portal');
    }
    finally { setOpeningPortal(false); }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(typeof ts === 'number' ? ts * 1000 : ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch(e) { return '—'; }
  };

  const statusColor = { active: '#166534', trialing: '#1e40af', past_due: '#dc2626', cancelled: '#991b1b', expired: '#991b1b' };
  const statusBg = { active: '#dcfce7', trialing: '#dbeafe', past_due: '#fef2f2', cancelled: '#fef2f2', expired: '#fef2f2' };

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto', borderTopColor: 'var(--panzix-green)' }} /></div>;

  const sub = billing?.subscription;
  const pm = billing?.payment_method;

  return (
    <div>
      {/* Current Subscription */}
      {sub ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{sub.plan_name || sub.plan}</span>
              <span style={{ marginLeft: 10, fontSize: 16, fontWeight: 700, color: 'var(--panzix-green)' }}>${sub.price}/mo</span>
            </div>
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: statusBg[sub.status] || '#f1f5f9', color: statusColor[sub.status] || '#475569', textTransform: 'capitalize' }}>
              {sub.status === 'trialing' ? '14-Day Trial' : sub.status}
            </span>
          </div>
          {billing.next_billing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              <Calendar size={14} />
              {sub.status === 'trialing' ? 'Trial ends' : 'Next billing'}: <strong>{formatDate(billing.next_billing)}</strong>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: '#fef3c7', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
          <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />No active subscription
        </div>
      )}

      {/* Payment Method */}
      {pm && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CreditCard size={20} color="#475569" />
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{pm.brand}</span>
              <span style={{ marginLeft: 8, color: '#64748b', fontSize: 13 }}>ending in {pm.last4}</span>
              {pm.exp_month > 0 && <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 12 }}>Exp {pm.exp_month}/{pm.exp_year}</span>}
            </div>
          </div>
          <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={12} />Default</span>
        </div>
      )}

      {/* Manage Billing Button */}
      {sub?.stripe_customer_id && (
        <button onClick={openCustomerPortal} disabled={openingPortal} className="btn-action" style={{ width: '100%', borderRadius: 10, fontSize: 14, padding: '12px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} data-testid="manage-billing">
          <ExternalLink size={15} />{openingPortal ? 'Opening Stripe Portal...' : 'Manage Billing, Cards & Subscription'}
        </button>
      )}

      {/* Invoice History */}
      {billing?.invoices?.length > 0 && (
        <div>
          <h5 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#475569' }}>Invoice History</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {billing.invoices.map(inv => (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={14} color="#64748b" />
                  <span style={{ fontWeight: 600 }}>${inv.amount.toFixed(2)}</span>
                  <span style={{ color: '#94a3b8' }}>{inv.number}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(inv.date)}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: inv.status === 'paid' ? '#dcfce7' : '#fef3c7', color: inv.status === 'paid' ? '#166534' : '#92400e' }}>{inv.status}</span>
                  {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}><Download size={14} /></a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
